import React, { useEffect, useState } from "react";
import { Check, Building2, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const Tag = ({ children }) => (
  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">{children}</span>
);

export default function AdminSector() {
  const { refresh } = useBranding();
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/sector-presets").then(({ data: d }) => setData(d))
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Could not load sector presets"));

  useEffect(() => { load(); }, []);

  const apply = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/sector-presets/apply", { sector: preview.key });
      setPreview(null);
      await Promise.all([load(), refresh()]);
      toast.success(`${preview.label} preset applied — ${res.lists_updated} lists replaced, ${res.frameworks_created} frameworks created`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      testId="admin-sector-page"
      title="Sector Presets"
      subtitle="Apply a sector configuration pack: terminology, RAID types, frameworks, stage labels, RAG areas and module visibility. Everything stays editable afterwards."
    >
      {!data ? (
        <Empty testId="sector-loading" text="Loading presets…" />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {data.presets.map((p) => {
            const applied = data.applied === p.key;
            const hidden = Object.entries(p.modules).filter(([, v]) => !v).map(([k]) => k);
            return (
              <div key={p.key} data-testid={`sector-card-${p.key}`} className={`panel p-5 transition-shadow hover:shadow-md ${applied ? "ring-2 ring-[color:var(--brand-primary)]" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-900">
                      <Building2 size={16} className="text-slate-400" /> {p.label}
                      {applied && (
                        <span data-testid={`sector-applied-${p.key}`} className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                          <Check size={10} /> Applied
                        </span>
                      )}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">{p.description}</p>
                  </div>
                  <Button size="sm" variant={applied ? "outline" : "default"} data-testid={`apply-sector-${p.key}`} onClick={() => setPreview(p)} className="shrink-0 gap-1.5" style={applied ? undefined : { backgroundColor: "var(--brand-primary)" }}>
                    <Sparkles size={13} /> {applied ? "Re-apply" : "Preview & Apply"}
                  </Button>
                </div>
                <dl className="mt-4 space-y-2 border-t border-slate-100 pt-3 text-xs">
                  <div>
                    <dt className="font-semibold text-slate-500">Terminology</dt>
                    <dd className="mt-1 flex flex-wrap gap-1">
                      {Object.entries(p.terminology).slice(0, 5).map(([k, v]) => <Tag key={k}>{k} → {v}</Tag>)}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-slate-500">Stages</dt>
                    <dd className="mt-1 text-slate-600">{(p.lists.stage || []).join(" → ")}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-slate-500">Frameworks</dt>
                    <dd className="mt-1 flex flex-wrap gap-1">{p.frameworks.map((f) => <Tag key={f.framework}>{f.framework}</Tag>)}</dd>
                  </div>
                  {hidden.length > 0 && (
                    <div>
                      <dt className="font-semibold text-slate-500">Modules hidden</dt>
                      <dd className="mt-1 flex flex-wrap gap-1">{hidden.map((m) => <Tag key={m}>{m.replace(/_/g, " ")}</Tag>)}</dd>
                    </div>
                  )}
                </dl>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="sector-preview-dialog" className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Apply the {preview?.label} preset</DialogTitle>
            <DialogDescription>
              This replaces the configurable list values below and rebuilds the governance framework templates.
              Existing project records are untouched, and you can edit any value afterwards in Admin → Lists or Admin → Frameworks.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="field-label">Terminology</h4>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(preview.terminology).map(([k, v]) => <Tag key={k}>{k} → {v}</Tag>)}
                </div>
              </div>
              <div>
                <h4 className="field-label">RAG area labels</h4>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(preview.rag_area_labels).map(([k, v]) => <Tag key={k}>{k.replace("_rag", "")} → {v}</Tag>)}
                </div>
              </div>
              {Object.entries(preview.lists).map(([key, values]) => (
                <div key={key}>
                  <h4 className="field-label">{key.replace(/_/g, " ")} <span className="text-slate-400">({values.length} values, replaces current)</span></h4>
                  <div className="flex flex-wrap gap-1">{values.map((v) => <Tag key={v}>{v}</Tag>)}</div>
                </div>
              ))}
              <div>
                <h4 className="field-label">Framework templates</h4>
                {preview.frameworks.map((f) => (
                  <p key={f.framework} className="text-xs text-slate-600">
                    <span className="font-semibold">{f.framework}</span> — {f.stages.length} stages, {f.standard_risks.length} standard risks
                  </p>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="sector-cancel-btn" onClick={() => setPreview(null)}>Cancel</Button>
            <Button data-testid="sector-confirm-btn" disabled={busy} onClick={apply} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
              <Sparkles size={14} /> {busy ? "Applying…" : "Apply Preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
